/* eslint-disable @typescript-eslint/no-unsafe-assignment */
import React, { useCallback, useEffect, useState } from "react";
import SessionStore from "../../../stores/SessionStore";
import { purgeSyncData } from "../../../utils/api/Purge";
import { Box, InlineNotification, Link, Text, useToast, useTranslation } from "@familyzone/component-library";
import { SyncStatus } from "./SyncStatus";
import { SyncCredentialStatus } from "./SyncCredentialStatus";
import PageWithHeader from "../../templates/PageWithHeader";
import { SubmitHandler, useForm, Controller } from "react-hook-form";
import {
  FormErrorMessage,
  FormLabel,
  FormControl,
  Button,
  Divider,
  Accordion,
  AccordionItem,
  AccordionButton,
  AccordionPanel,
  AccordionIcon,
  Select,
  Tooltip,
  Flex,
} from "@chakra-ui/react";
import DumbBusyIndicator from "../../../modules/DumbBusyIndicator";
import { CheckBoxControl } from "./ConfigForm/CheckboxControl";
import { CheckboxTextComboControl } from "./ConfigForm/CheckboxTextComboControl";
import { AdvanceConfigurationConfirmModal } from "./ConfigForm/AdvanceConfigurationConfirmModal";
import { PurgeConfirmModal } from "./ConfigForm/PurgeConfirmModal";
import { MergeOptions, UserMergeKey } from "./ConfigForm/MergeOptions";
import { MergeConfig } from "../sis-integration/SisIntegration";
import { colors } from "../../../theme";
import { useSessionStore } from "../../../storez/SessionStore";
import { executeSync, extractSyncError, getSyncByType, revokeCredential, startOAuth, SyncType } from "../../../utils/api/SyncApi";
import { isCredentialLinked, mapNodeToAzureInputs, saveAzureSync } from "./googleAzureSyncMapper";

export interface ConfigurationResponse {
  configuration: Configuration;
}

interface Status {
  deviceid: string;
  azuread_sync_state?: "finished" | "error" | "running";
  azuread_sync_start_time?: number;
  azuread_sync_end_time?: number;
  azuread_sync_errors?: string;
  azuread_sync_group_count?: number;
  azuread_sync_user_count?: number;
}

interface Configuration {
  azuread_access_token: string;
  azuread_domain: string;
  azuread_enabled: boolean;
  azuread_group_name_field: string;
  azuread_refresh_token: string;
  azuread_strip_domain: boolean;
  azuread_sync_end_time: number;
  azuread_sync_errors: string;
  azuread_sync_group_count: number;
  azuread_sync_hidden_group_memberships: boolean;
  azuread_sync_start_time: number;
  azuread_sync_state: string;
  azuread_sync_user_count: number;
  status: Status;
  azure_merge_config?: MergeConfig;
  azuread_sync_id?: string;
  azuread_domain_filter_enabled?: boolean;
  azuread_domain_filter_value?: string;
  azuread_ignore_empty_groups?: boolean;
}

export enum GroupNameField {
  displayName = "displayName",
  description = "description",
}

export type AzureConfigInputs = {
  enabled: boolean;
  name?: string;
  strip_domain: boolean;
  strip_domain_value?: string; // these are commas seperated lists
  sync_hidden_group_memberships: boolean;
  group_name_field: GroupNameField;
  user_merge_key: UserMergeKey;
  user_merge_priority: number;
  user_merge_expression?: string;
  user_merge_attach_only: boolean;
  azuread_sync_id: string;
  domain_filter_enabled: boolean;
  domain_filter_value?: string; // comma separated list
  ignore_empty_groups?: boolean;
};

interface AzureConfigurationProps {
  location?: {
    state?: {
      linkError?: string;
    };
  };
}

export const AzureConfiguration: React.FC<AzureConfigurationProps> = ({ location }) => {
  const [tokenExists, setTokenExists] = useState<boolean>(false);
  const { t } = useTranslation();
  const { successToast, errorToast } = useToast();
  const [showConfirmModal, setShowConfirmModal] = useState<boolean>(false);
  const [showPurgeModal, setShowPurgeModal] = useState<boolean>(false);
  const [isPurging, setIsPurging] = useState<boolean>(false);
  const isSupportAdmin = useSessionStore.getState().isSupportAdmin();

  const _handleLoad = useCallback(async (): Promise<AzureConfigInputs> => {
    // SM-API: resolve the singleton AZURE sync node and map it into the form shape.
    const node = await getSyncByType(SyncType.Azure);
    if (!node) {
      setTokenExists(false);
      return {
        enabled: false,
        strip_domain: false,
        strip_domain_value: "",
        domain_filter_enabled: false,
        domain_filter_value: "",
        sync_hidden_group_memberships: false,
        group_name_field: GroupNameField.displayName,
        user_merge_priority: 50,
        user_merge_expression: undefined,
        user_merge_key: UserMergeKey.Email,
        user_merge_attach_only: false,
        azuread_sync_id: "",
        ignore_empty_groups: false,
      };
    }
    setTokenExists(isCredentialLinked(node));
    return mapNodeToAzureInputs(node);
  }, []);

  const {
    register,
    handleSubmit,
    watch,
    control,
    reset,
    setValue,
    formState: { errors, isDirty, isSubmitting, isLoading, dirtyFields },
  } = useForm<AzureConfigInputs>({
    mode: "onBlur",
    defaultValues: _handleLoad, // we use an async func here so the form "isLoading" state is true while doing initial load
  });

  const enabled = watch("enabled");
  const stripDomain = watch("strip_domain");
  const domainFilterEnabled = watch("domain_filter_enabled");
  const mergeKey = watch("user_merge_key");
  const azuread_sync_id = watch("azuread_sync_id");

  // eslint-disable-next-line @typescript-eslint/no-unsafe-call,@typescript-eslint/no-unsafe-member-access
  const device: string = SessionStore.getSelectedDevice() as string;

  const breadcrumbs = [
    { title: t("Configuration"), url: "/config", isActive: false },
    { title: t("Authentication"), url: "/config/device/authentication/portals", isActive: false },
    { title: t("Microsoft Entra ID"), isActive: true },
  ];

  // Initial load is handled by react-hook-form's async `defaultValues` (_handleLoad).
  // handleLoad is used to re-load the form after save/revoke/sync/purge.
  const handleLoad = useCallback(async (): Promise<void> => {
    reset(await _handleLoad());
  }, [reset, _handleLoad]);

  const linkError = location?.state?.linkError;

  useEffect(() => {
    if (linkError) {
      errorToast({
        title: t("Failed to link Microsoft Entra ID credentials"),
        description: t(linkError),
      });
    }
  }, [linkError, errorToast, t]);

  useEffect(() => {
    if (domainFilterEnabled) {
      setValue("ignore_empty_groups", true, { shouldTouch: true, shouldValidate: true, shouldDirty: true });
    }
  }, [setValue, domainFilterEnabled]);

  const _submit = useCallback(
    (input: AzureConfigInputs) => {
      return new Promise<void>((resolve, reject) => {
        // SM-API: create-or-update the singleton AZURE sync.
        saveAzureSync(input)
          .then(async () => {
            successToast({ title: t("Saved Entra ID configuration") });
            await handleLoad();
            resolve();
          })
          .catch((e) => {
            errorToast({
              title: t("Failed to save Entra ID configuration"),
              description: t(extractSyncError(e, "An unknown error occurred while trying to save configuration.")),
            });
            reject("save failed");
          });
      });
    },
    [handleLoad, successToast, errorToast, t]
  );

  const onSubmit: SubmitHandler<AzureConfigInputs> = async (data) => {
    // If we are already showing the modal, submit it
    if (showConfirmModal) {
      setShowConfirmModal(false);
      return _submit(data);
    }
    if (dirtyFields.strip_domain || dirtyFields.strip_domain_value) {
      setShowConfirmModal(true);
      return;
    }
    return _submit(data);
  };

  const handleSubmitConfirmationConfirm = () => {
    void handleSubmit(onSubmit)();
  };

  const handleRevoke = () => {
    void (async () => {
      try {
        if (azuread_sync_id) await revokeCredential(azuread_sync_id);
        await handleLoad();
      } catch (e) {
        errorToast({
          title: t("Failed to revoke Entra ID credentials"),
          description: t(extractSyncError(e, "Could not revoke credentials. Please try again.")),
        });
      }
    })();
  };

  const handleLink = () => {
    // SM-API OAuth: start returns an authorization URL + single-use state to persist.
    void (async () => {
      if (!azuread_sync_id) {
        errorToast({
          title: t("Save the configuration first"),
          description: t("Please save the sync configuration before linking credentials."),
        });
        return;
      }
      try {
        const redirectURI = `${window.location.origin}/config/login/admin/azuread/callback`;
        const auth = await startOAuth(azuread_sync_id, redirectURI);
        localStorage.setItem("oauth_state_AZURE", auth.state);
        window.location.href = auth.authorizationURL;
      } catch (e) {
        errorToast({
          title: t("Failed to link Microsoft Entra ID credentials"),
          description: t(extractSyncError(e, "Could not start credential linking. Please try again.")),
        });
      }
    })();
  };

  const handleSync = useCallback(() => {
    void (async () => {
      if (azuread_sync_id) {
        try {
          await executeSync(azuread_sync_id);
          const manuallySetRunningSyncs = JSON.parse(localStorage.getItem("manuallySetRunningSyncs") || "{}");
          const now = new Date();
          // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
          manuallySetRunningSyncs[device.concat("_AzureLockout")] = now.getTime() + 60000; // 60 second 'ttl' to lock out this sync in this browser
          localStorage.setItem("manuallySetRunningSyncs", JSON.stringify(manuallySetRunningSyncs));
        } catch {
          errorToast({ title: t("Failed to start sync"), description: t("The sync could not be started. Please try again.") });
        }
      }
      await handleLoad();
    })();
  }, [azuread_sync_id, device, handleLoad, errorToast, t]);

  const handlePurge = useCallback(async () => {
    setIsPurging(true);
    try {
      await purgeSyncData(azuread_sync_id, device);
      successToast({
        title: t("Purge triggered successfully"),
      });
      setShowPurgeModal(false);
      await handleLoad();
    } catch (error) {
      errorToast({
        title: t("Failed to trigger purge"),
        description: t(error instanceof Error ? error.message : "An unknown error occurred while trying to purge data."),
      });
    } finally {
      setIsPurging(false);
    }
  }, [azuread_sync_id, device, successToast, errorToast, t, handleLoad]);

  return (
    <PageWithHeader title={"Microsoft Entra ID"} breadcrumbs={breadcrumbs}>
      <Flex mr="sp24" justifyContent="end">
        {isSupportAdmin && azuread_sync_id && (
          <Button variant="danger" onClick={() => setShowPurgeModal(true)} data-testid="purge-btn">
            Purge Data
          </Button>
        )}
      </Flex>
      <Box display={"flex"} flex={1} padding={"24px"} gap={"16px"}>
        <Box flex={1} minWidth={"500px"}>
          <Box background={"white"} display={"flex"} flexDir={"column"} gap={"1rem"} padding={"1rem"} borderRadius={"6px"}>
            <Box padding={"20px 20px 0 20px"} display={"flex"} flexDir={"row"} gap={"4px"}>
              <Text fontSize={14}>
                {"Find out how to"}{" "}
                <Link isExternal href={"https://help.linewize.com/hc/en-gb/articles/5732866996124"}>
                  sync Microsoft Entra ID with Linewize Filter
                </Link>
              </Text>
            </Box>
            {isLoading && (
              <Box display={"flex"} justifyContent={"center"} flex={1}>
                <DumbBusyIndicator loaded={!isLoading} />
              </Box>
            )}
            {!isLoading && (
              // eslint-disable-next-line @typescript-eslint/no-misused-promises
              <form onSubmit={handleSubmit(onSubmit)} data-testid={"azure-config-form"}>
                <Box sx={{ display: "flex", flexDirection: "column", padding: "24px", gap: "16px", fontSize: "14px" }}>
                  <Box
                    sx={{
                      minHeight: "40px",
                      backgroundColor: colors.neutrals["20"],
                      alignContent: "center",
                      padding: "12px",
                    }}
                  >
                    <Text fontWeight={500}>General</Text>
                  </Box>

                  <CheckBoxControl
                    fieldProps={{
                      name: "enabled",
                      label: "Enabled",
                      tooltip: "When ticked, this Entra ID sync will run.",
                      control: control,
                      errors: errors,
                    }}
                  />

                  {enabled && (
                    <>
                      <Box
                        sx={{
                          minHeight: "40px",
                          backgroundColor: colors.neutrals["20"],
                          alignContent: "center",
                          padding: "12px",
                        }}
                      >
                        <Text fontWeight={500}>Groups</Text>
                      </Box>
                      <CheckBoxControl
                        fieldProps={{
                          name: "sync_hidden_group_memberships",
                          label: "Sync Hidden Group Memberships",
                          tooltip: 'Syncs groups created by School Data Sync (SDS) with the "HiddenMemberships" property.',
                          control: control,
                          errors: errors,
                        }}
                      />

                      <Controller
                        control={control}
                        name="group_name_field"
                        rules={{ required: "Please select a group name field." }}
                        render={({ field: { onChange, onBlur, value, name, ref }, fieldState: { error } }) => (
                          <FormControl
                            isInvalid={!!errors.group_name_field}
                            sx={{
                              display: "flex",
                              minHeight: "40px",
                              gap: "16px",
                            }}
                          >
                            <FormLabel
                              htmlFor="group_name_field"
                              sx={{
                                width: "120px",
                                alignContent: "center",
                                flexShrink: "0",
                              }}
                            >
                              Group Name Field
                            </FormLabel>
                            <Select
                              data-testid={"name-field-select"}
                              name={name}
                              ref={ref}
                              iconSize={"0"}
                              onChange={onChange}
                              onBlur={onBlur}
                              value={value}
                              size="lg"
                              variant={"outline"}
                              defaultValue={"displayName"}
                              sx={{
                                width: "100%",
                                height: "40px",
                                backgroundColor: "white",
                                borderRadius: "6px",
                                border: `1px solid ${colors.neutrals["30"]}`,
                                paddingLeft: "16px",
                              }}
                            >
                              <option value={"displayName"}>Group Name</option>
                              <option value={"description"}>Group Description</option>
                            </Select>
                            <FormErrorMessage>{error && error.message}</FormErrorMessage>
                          </FormControl>
                        )}
                      />

                      {/* by providing the defaultIndex we can start this expanded if they have strip domain already enabled */}
                      <Accordion
                        allowToggle
                        defaultIndex={stripDomain ? 0 : undefined}
                        sx={{
                          border: `1px solid ${colors.neutrals["20"]}`,
                          borderRadius: "6px",
                        }}
                      >
                        <AccordionItem>
                          <AccordionButton>
                            <Box
                              sx={{
                                display: "flex",
                                width: "100%",
                                minHeight: "40px",
                                backgroundColor: colors.neutrals["20"],
                                alignContent: "center",
                                alignItems: "center",
                                padding: "12px",
                                gap: "8px",
                              }}
                            >
                              <AccordionIcon />

                              <Text fontWeight={500}>Advanced Configuration</Text>
                            </Box>
                          </AccordionButton>
                          <AccordionPanel
                            sx={{
                              display: "flex",
                              flexDirection: "column",
                              padding: "16px",
                              fontSize: "14px",
                              gap: "8px",
                            }}
                          >
                            <Text>
                              Advanced sync settings are for complex deployments only. Consult with the Linewize support team before making
                              any changes.
                            </Text>
                            <Divider borderColor="neutrals.40" />
                            <MergeOptions errors={errors} register={register} control={control} mergeKey={mergeKey} />
                            <Divider borderColor="neutrals.40" />

                            {stripDomain && (
                              <InlineNotification
                                status="warning"
                                notificationTitle={"User names need to be unique when using strip domains. "}
                                notificationDescription={
                                  "Strip Domain is used to modify usernames from the directory. Before enabling this feature ensure usernames without domains are unique. If usernames are not unique this will result in errors."
                                }
                              />
                            )}
                            <CheckboxTextComboControl
                              label="Domain Strip"
                              tooltip="Accepts CSV list of domains. Users within these domains will be imported as `username` instead of `username@domain.com`."
                              control={control}
                              errors={errors}
                              checkboxFieldProps={{
                                name: "strip_domain",
                                testId: "strip_domain-checkbox",
                              }}
                              inputFieldProps={{
                                name: "strip_domain_value",
                                testId: "strip_domain-input",
                                disabled: !stripDomain,
                                rules: {
                                  required: stripDomain ? "Domains are required if Strip Domain is enabled." : false,
                                  pattern: {
                                    value: /^(?!.*:\/\/)(([^,\s]+\s*,\s*)*[^,\s]+)$/,
                                    message: "Please enter domains without a protocol, separated by commas.",
                                  },
                                },
                              }}
                            />

                            <CheckboxTextComboControl
                              label="Domain Filter"
                              tooltip="Accepts CSV list of domains. Only users and groups within these domains will be synced."
                              control={control}
                              errors={errors}
                              checkboxFieldProps={{
                                name: "domain_filter_enabled",
                                testId: "domain_filter-checkbox",
                              }}
                              inputFieldProps={{
                                name: "domain_filter_value",
                                testId: "domain_filter-input",
                                disabled: !domainFilterEnabled,
                                rules: {
                                  required: domainFilterEnabled ? "Domains are required if Domain Filter is enabled." : false,
                                  pattern: {
                                    value: /^(?!.*:\/\/)(([^,\s]+\s*,\s*)*[^,\s]+)$/,
                                    message: "Please enter domains without a protocol, separated by commas.",
                                  },
                                },
                              }}
                            />

                            <Flex>
                              <Tooltip
                                hasArrow
                                label="Domain Filter forces Ignore Empty Groups to be enabled."
                                variant="dark"
                                placement="top"
                              >
                                <span>
                                  <CheckBoxControl
                                    fieldProps={{
                                      name: "ignore_empty_groups",
                                      label: "Ignore Empty Groups",
                                      tooltip: "When ticked, empty groups won't be synced.",
                                      control: control,
                                      errors: errors,
                                      disabled: domainFilterEnabled,
                                    }}
                                  />
                                </span>
                              </Tooltip>
                            </Flex>
                          </AccordionPanel>
                        </AccordionItem>
                      </Accordion>
                    </>
                  )}
                </Box>

                <Box
                  sx={{
                    padding: "24px",
                    background: "#F5FCFF",
                    display: "flex",
                    gap: "24px",
                    alignItems: "center",
                  }}
                >
                  <Button
                    data-testid={"submitBtn"}
                    mt={4}
                    disabled={!isDirty || isSubmitting}
                    colorScheme="teal"
                    type="submit"
                    variant={"primary"}
                  >
                    Submit
                  </Button>
                  <DumbBusyIndicator loaded={!isSubmitting} />
                </Box>
                <AdvanceConfigurationConfirmModal
                  isOpen={showConfirmModal}
                  onClose={() => setShowConfirmModal(false)}
                  onSubmit={handleSubmitConfirmationConfirm}
                />
              </form>
            )}
          </Box>
        </Box>
        {enabled && (
          <Box width={"450px"} gap={"16px"} display={"flex"} flexDir={"column"}>
            <SyncStatus
              syncType={"AZURE"}
              syncId={azuread_sync_id || undefined}
              handleRunSync={tokenExists && !isDirty ? handleSync : undefined}
              syncLockoutTime={
                device
                  ? // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
                    JSON.parse(localStorage.getItem("manuallySetRunningSyncs") || "{}")[device.concat("_AzureLockout")]
                    ? // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
                      JSON.parse(localStorage.getItem("manuallySetRunningSyncs") || "{}")[device.concat("_AzureLockout")]
                    : undefined
                  : undefined
              }
            />
            <SyncCredentialStatus
              isLinked={tokenExists}
              handleLink={handleLink}
              handleRevoke={handleRevoke}
              syncType={"AZURE"}
              linkDisabled={!azuread_sync_id || isDirty}
              linkDisabledReason={
                !azuread_sync_id
                  ? "Save the sync configuration before linking credentials."
                  : "Save your changes before linking credentials."
              }
            />
          </Box>
        )}
      </Box>
      <PurgeConfirmModal
        isOpen={showPurgeModal}
        onClose={() => setShowPurgeModal(false)}
        onConfirm={() => void handlePurge()}
        isLoading={isPurging}
      />
    </PageWithHeader>
  );
};
